/**
 * Connections · System (`/services/system`): the workspace's System, the
 * one running copy every Team shares, from `GET /api/system-environments/system`.
 *
 *   headline       the System's state, in the Running system section's words
 *   repositories   one row per repository: what a pull request would start
 *                  from (the newest build proven from a fresh start), what is
 *                  building or did not build now, when it was last refreshed,
 *                  and which Teams work in it
 *   needs          what only a person can answer, open ones first; they are
 *                  answered on Waiting on you
 *
 * The tab exists only where the read answers: a 404 means running copies are
 * off for the workspace (the per-workspace flag the client cannot read), and
 * the page shows no chip for it. Markup only: the words are `systemCopy.ts`.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { SystemNeed, SystemWorkspaceReadResponse } from "@yagni/shared";
import { Group, Groups, Row } from "../queue/index.js";
import { captureUiError } from "../sentry.js";
import type { SystemApi } from "./system/systemApi.js";
import {
  DECLINED,
  NEED_KIND_WORD,
  PROVIDED,
  REPOSITORIES_GROUP,
  SYSTEM_EMPTY,
  SYSTEM_NEEDS_GROUP,
  SYSTEM_NEEDS_HINT,
  SYSTEM_TAB_SUB,
  freshnessLine,
  repositoryBuildLine,
  repositoryRefreshedLine,
  repositoryStateWord,
  systemHeadline,
  teamsLine,
} from "./system/systemCopy.js";

export type WorkspaceSystemStatus = "loading" | "off" | "ready" | "error";

/** The workspace System, read once and again on each Refresh beat. */
export function useWorkspaceSystem(api: SystemApi, refreshKey: number) {
  const [status, setStatus] = useState<WorkspaceSystemStatus>("loading");
  const [data, setData] = useState<SystemWorkspaceReadResponse | null>(null);
  const latest = useRef(0);
  const read = useCallback(async () => {
    const call = ++latest.current;
    try {
      const next = await api.readWorkspace();
      if (call !== latest.current) return;
      setData(next);
      setStatus(next ? "ready" : "off");
    } catch (error) {
      captureUiError(error, { area: "connectors", operation: "read_workspace_system" });
      if (call !== latest.current) return;
      setStatus((prev) => (prev === "ready" ? prev : "error"));
    }
  }, [api]);
  useEffect(() => {
    void read();
    return () => {
      latest.current += 1;
    };
  }, [read, refreshKey]);
  return { status, data };
}

function needStatusWord(need: SystemNeed): string {
  if (need.status === "provided") return PROVIDED;
  if (need.status === "declined") return DECLINED;
  return NEED_KIND_WORD[need.kind];
}

export function SystemTab({ data, now }: { data: SystemWorkspaceReadResponse; now?: number }) {
  const { system, repositories, components, needs } = data;
  const names = new Map(components.map((c) => [c.key, c.name]));
  const open = needs.filter((need) => need.status === "open");
  const answered = needs.filter((need) => need.status !== "open");
  const freshness = freshnessLine(system, now);
  return (
    <Groups>
      <div className="blk" data-testid="system-tab">
        <p className="qa-hint">{SYSTEM_TAB_SUB}</p>
        <p data-testid="system-tab-headline" data-state={system?.state ?? "none"}>
          {system ? systemHeadline(system, components) : SYSTEM_EMPTY}
        </p>
        {freshness && (
          <p className="fact" data-testid="system-tab-freshness">
            {freshness}
          </p>
        )}
      </div>
      <Group label={REPOSITORIES_GROUP} plain>
        {repositories.map((repo) => (
          <Row
            key={repo.repoId}
            title={repo.fullName ?? "A removed repository"}
            tag={{ label: repositoryStateWord(repo) }}
            state={[repositoryBuildLine(repo, now), teamsLine(repo)].filter(Boolean).join(" · ")}
            when={repositoryRefreshedLine(repo, now)}
            verb={null}
            className="system-repository"
          />
        ))}
      </Group>
      <Group
        label={SYSTEM_NEEDS_GROUP}
        tone="needs"
        plain
        aside={open.length > 0 ? <span className="fact">{SYSTEM_NEEDS_HINT}</span> : undefined}
      >
        {[...open, ...answered].map((need) => (
          <Row
            key={need.id}
            rowKey={need.name}
            title={need.detail || need.name}
            tag={{ label: needStatusWord(need) }}
            state={
              need.componentKey ? (names.get(need.componentKey) ?? need.componentKey) : undefined
            }
            verb={null}
            className="system-need"
          />
        ))}
      </Group>
    </Groups>
  );
}

/** The tab's body for the page: nothing until the read answers, a quiet line if it fails. */
export function SystemTabBody({
  status,
  data,
  now,
}: {
  status: WorkspaceSystemStatus;
  data: SystemWorkspaceReadResponse | null;
  now?: number;
}) {
  if (status === "ready" && data) return <SystemTab data={data} now={now} />;
  // Off (the read answered 404) renders nothing, as the chip does: the tab
  // exists only where the workspace has running copies.
  if (status === "error") {
    return (
      <div className="qlist">
        <div className="empty dashed tight" role="alert" data-testid="system-tab-empty">
          <p>The System could not be read. Try Refresh.</p>
        </div>
      </div>
    );
  }
  return null;
}
